import React, { useMemo, useState } from 'react';
import styles from '../NDAGenerator.module.scss';
import { useAppDispatch, setGeneratingExtraction } from 'app/redux';
import UploadDocumentModal from 'app/components/Modals/UploadDocumentModal/UploadDocumentModal';
import { notify } from '@zurich-base-ui/components.toast';
import { displayToastError } from 'app/utils/functions';
import { ndaApi } from 'app/api/ndaGeneratorApi';
import ResultsPanel from './ResultsPanel';
import UploadSection from './UploadSection';
import { extractDocumentText } from '../utils/textExtraction';

interface SectionAnalysisItem {
  name: string;
  score: number;
  status: 'missing' | 'compliant' | 'partial';
  findings: string[];
  explanation?: string;
  suggestedText?: string;
  recommendations: string[];
}

interface EnhancedManualResult {
  customerDocument: { name: string; sections: string[]; wordCount?: number };
  template: {
    name: string;
    type: 'bilateral' | 'unilateral';
    sections: string[];
  };
  analysis: {
    overallScore: number;
    complianceLevel: string;
    riskLevel: 'low' | 'medium' | 'high' | 'critical';
    overallAssessment?: string;
    strengths: string[];
    weaknesses: string[];
    recommendations: string[];
    missingSections: string[];
    nonCompliantSections: string[];
    suggestions: { toAdd: string[]; toModify: string[]; toRemove: string[] };
  };
  timestamp: string;
  analysisMethod: 'manual';
  comparisonSummary: {
    compliantSections: number;
    nonCompliantSections: number;
    missingSections: number;
  };
  sectionAnalysis: SectionAnalysisItem[];
}

interface ReviewModeProps {
  generatedDocumentText?: string;
}

export const ReviewMode: React.FC<ReviewModeProps> = ({
  generatedDocumentText
}) => {
  const dispatch = useAppDispatch();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [currentUploadType, setCurrentUploadType] = useState<
    'customer' | 'template'
  >('customer');
  const [customerNDAFile, setCustomerNDAFile] = useState<File | null>(null);
  const [customerTextFromUpload, setCustomerTextFromUpload] = useState<
    string | null
  >(null);
  const [zurichTemplateFile, setZurichTemplateFile] = useState<File | null>(
    null
  );
  const [templateDocumentId, setTemplateDocumentId] = useState<string | null>(
    null
  );
  const [zurichTextFromUpload, setZurichTextFromUpload] = useState<
    string | null
  >(null);

  const [uploadingCustomer, setUploadingCustomer] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState('');
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [complianceAnalysis, setComplianceAnalysis] = useState<any>(null);
  const [comparisonId, setComparisonId] = useState<string | null>(null);

  const openUploadModal = (type: 'customer' | 'template'): void => {
    setCurrentUploadType(type);
    setIsUploadModalOpen(true);
  };

  const onFileUpload = async (file: File): Promise<void> => {
    setComplianceAnalysis(null);
    setComparisonId(null);
    setAnalysisError(null);
    setIsUploadModalOpen(false);
    try {
      dispatch(setGeneratingExtraction(true));
      if (currentUploadType === 'customer') {
        setUploadingCustomer(true);
        setAnalysisProgress('Uploading customer NDA...');
        const res = await ndaApi.uploadCustomer(file);
        const docId = res.documentId;
        if (!docId?.trim()) {
          throw new Error(
            'Customer upload missing documentId/customerId (empty id)'
          );
        }
        setCustomerNDAFile(file);
        setCustomerTextFromUpload(res.customerText ?? null);
        notify({
          type: 'success',
          message: `Customer NDA uploaded (${file.name})`
        });
      } else {
        setAnalysisProgress('Uploading Zurich template...');
        const resZurich = await ndaApi.uploadZurich(file);
        const docId = resZurich.documentId;
        if (!docId?.trim()) {
          throw new Error(
            'Zurich template upload missing documentId/zurichId (empty id)'
          );
        }
        setZurichTemplateFile(file);
        setTemplateDocumentId(docId);
        setZurichTextFromUpload(resZurich.zurichText ?? null);
        notify({
          type: 'success',
          message: `Zurich template uploaded (${file.name})`
        });
      }
    } catch (e: any) {
      const msg = e?.message ?? 'Upload failed';
      setAnalysisError(msg);
      displayToastError(msg);
    } finally {
      setAnalysisProgress('');
      setUploadingCustomer(false);
      dispatch(setGeneratingExtraction(false));
    }
  };

  const removeFile = (which: 'customer' | 'template'): void => {
    if (which === 'customer') {
      setCustomerNDAFile(null);
    } else {
      setZurichTemplateFile(null);
      setTemplateDocumentId(null);
    }
  };

  const analyze = async (): Promise<void> => {
    if (uploadingCustomer) {
      displayToastError('Please wait for customer upload to finish.');
      return;
    }
    const sourceText = (
      customerTextFromUpload ??
      generatedDocumentText ??
      ''
    ).trim();
    const customerText = customerTextFromUpload
      ? sourceText
      : extractDocumentText(sourceText);
    if (!customerText) {
      displayToastError(
        'No NDA text available. Please generate an NDA or paste/upload NDA text in the editor before analyzing.'
      );
      return;
    }
    try {
      setIsAnalyzing(true);
      setAnalysisProgress('Analyzing compliance...');
      setAnalysisError(null);
      dispatch(setGeneratingExtraction(true));
      const analysis = await ndaApi.analyzeComplianceText({
        customerText,
        zurichId: templateDocumentId ?? undefined
      });
      // eslint-disable-next-line no-console
      console.debug('[NDAGenerator] analyze completed', analysis);
      setComplianceAnalysis(analysis);
      setComparisonId(analysis.comparisonId ?? null);
      notify({ type: 'success', message: 'Document analysis completed' });
    } catch (e: any) {
      const msg = e?.message ?? 'Analysis failed';
      setAnalysisError(msg);
      displayToastError(msg);
    } finally {
      setIsAnalyzing(false);
      setAnalysisProgress('');
      dispatch(setGeneratingExtraction(false));
    }
  };

  const startNewReview = (): void => {
    setCustomerNDAFile(null);
    setZurichTemplateFile(null);
    setTemplateDocumentId(null);
    setComplianceAnalysis(null);
    setComparisonId(null);
    setAnalysisError(null);
  };

  const hasDetailedComparison = useMemo<boolean>(() => {
    if (!complianceAnalysis?.sideBySideComparison) return false;
    return true;
  }, [complianceAnalysis]);

  const showComparisonResult = useMemo<boolean>(() => {
    const files = complianceAnalysis?.comparisonResultFiles;
    const hasFiles = files && Object.keys(files).length > 0;
    const noDetailed =
      !complianceAnalysis?.sideBySideComparison || !hasDetailedComparison;
    return Boolean(noDetailed && hasFiles);
  }, [complianceAnalysis, hasDetailedComparison]);

  const enhancedResult = useMemo<EnhancedManualResult | null>(() => {
    if (!complianceAnalysis) return null;
    const summary =
      complianceAnalysis.summary ?? complianceAnalysis.result?.summary;
    const findings: any[] =
      complianceAnalysis.findings ?? complianceAnalysis.result?.findings ?? [];

    const totalClauses: number =
      summary?.totalClauses !== undefined && summary?.totalClauses !== null
        ? Number(summary.totalClauses)
        : (() => {
            const matches = Number(summary?.matches ?? 0);
            const nonCompliant = Number(summary?.nonCompliant ?? 0);
            const missing = Number(summary?.missing ?? 0);
            const sum = matches + nonCompliant + missing;
            return sum > 0 ? sum : findings.length;
          })();
    const missingCount: number =
      summary?.missing ?? findings.filter((f) => f.status === 'missing').length;
    const nonCompliantCount: number =
      summary?.nonCompliant ??
      findings.filter((f) => f.status === 'non-compliant').length;
    const matchesCount: number =
      summary?.matches ??
      findings.filter((f) => f.status === 'match' || f.status === 'compliant')
        .length;

    const compliantSections = matchesCount;
    const overallScore =
      totalClauses > 0
        ? Math.round((compliantSections / totalClauses) * 100)
        : 0;
    const totalGaps = nonCompliantCount + missingCount;
    let riskLevel: 'low' | 'medium' | 'high' | 'critical';
    if (totalGaps > 8) riskLevel = 'critical';
    else if (totalGaps > 5) riskLevel = 'high';
    else if (totalGaps > 0) riskLevel = 'medium';
    else riskLevel = 'low';

    const sectionAnalysis: SectionAnalysisItem[] = findings.map((f) => {
      const status = f.status as string | undefined;
      let mapped: 'missing' | 'compliant' | 'partial';
      if (status === 'missing') mapped = 'missing';
      else if (status === 'non-compliant') mapped = 'partial';
      else if (status === 'match' || status === 'compliant')
        mapped = 'compliant';
      else mapped = 'partial';
      let score = 0;
      if (mapped === 'compliant') score = 100;
      else if (mapped === 'partial') score = 25;
      const heading: string = (f.title ?? f.heading ?? f.name ?? f.clause ?? '')
        .toString()
        .trim();
      const explanationStr = (f.explanation ?? f.notes ?? '').toString().trim();
      const suggestedStr = (f.suggestedText ?? '').toString().trim();
      return {
        name: heading,
        score,
        status: mapped,
        findings: [f.clause ?? ''],
        explanation: explanationStr.length ? explanationStr : undefined,
        suggestedText: suggestedStr.length ? suggestedStr : undefined,
        recommendations: f.notes ? [f.notes] : []
      };
    });

    const result: EnhancedManualResult = {
      customerDocument: {
        name: customerNDAFile?.name ?? 'Customer NDA',
        sections: [],
        wordCount: undefined
      },
      template: {
        name: 'Baseline Template',
        type: 'bilateral',
        sections: []
      },
      analysis: {
        overallScore,
        complianceLevel: riskLevel === 'critical' ? 'high' : riskLevel,
        riskLevel,
        overallAssessment: undefined,
        strengths: [],
        weaknesses: [],
        recommendations: [],
        missingSections: [],
        nonCompliantSections: [],
        suggestions: { toAdd: [], toModify: [], toRemove: [] }
      },
      timestamp: new Date().toISOString(),
      analysisMethod: 'manual',
      comparisonSummary: {
        compliantSections,
        nonCompliantSections: nonCompliantCount,
        missingSections: missingCount
      },
      sectionAnalysis
    };
    return result;
  }, [complianceAnalysis, customerNDAFile, zurichTextFromUpload]);

  return (
    <div className={styles.reviewPanel} style={{ width: '100%' }}>
      <h2>NDA Compliance Review</h2>
      {complianceAnalysis ? (
        <ResultsPanel
          complianceAnalysis={complianceAnalysis}
          hasDetailedComparison={hasDetailedComparison}
          showComparisonResult={showComparisonResult}
          enhancedResult={enhancedResult}
          customerNDAName={customerNDAFile?.name ?? 'Unknown NDA'}
          comparisonId={comparisonId}
          onCloseEnhanced={() => setComplianceAnalysis(null)}
          onStartNewReview={startNewReview}
        />
      ) : (
        <UploadSection
          customerNDAFile={customerNDAFile}
          zurichTemplateFile={zurichTemplateFile}
          analysisError={analysisError}
          isAnalyzing={isAnalyzing}
          uploadingCustomer={uploadingCustomer}
          analyzeTitle={(() => {
            const hasCustomerText = Boolean(
              (generatedDocumentText ?? customerTextFromUpload ?? '').trim()
            );
            if (uploadingCustomer) return 'Uploading customer NDA...';
            if (!hasCustomerText)
              return 'Provide NDA text by generating or uploading a customer NDA';
            return 'Analyze document';
          })()}
          onOpenUploadModal={openUploadModal}
          onRemoveFile={removeFile}
          onAnalyze={analyze}
          analyzeDisabled={(() => {
            const hasCustomerText = Boolean(
              (generatedDocumentText ?? customerTextFromUpload ?? '').trim()
            );
            return isAnalyzing || uploadingCustomer || !hasCustomerText;
          })()}
          analysisProgress={analysisProgress}
        />
      )}
      <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={onFileUpload}
        acceptedFileTypes={{
          'application/pdf': ['.pdf'],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            ['.docx'],
          'application/msword': ['.doc']
        }}
        title={
          currentUploadType === 'customer'
            ? 'Upload Customer/Partner NDA (NOT Zurich Templates)'
            : 'Upload Custom Zurich Template (Optional)'
        }
        showLanguageSelector={false}
      />
    </div>
  );
};

export default ReviewMode;
